import * as React from 'react';
import type { KitType } from '../actions/getKitTypes';
import { sendAdminRequest, sendRequest } from '../api/websocketApi';
import { useStore } from '../store/store';

export interface Kit {
    KitId: number;
    SampleId: string;
    SampleRegistrationDate: Date;
    Nickname: string;
    LabProcessStatus: LabProcessStatus;
    InsertionDate: Date;
    KitTypeId?: number;
    StatusUpdateDate?: Date;
    TrfFilePath?: string;
    TrfFileStatus?: 'submitted' | 'invalid' | 'valid';
}

export enum LabProcessStatus {
    Ordered = 'Ordered',
    Registration = 'Registration',
    Extraction = 'Extraction',
    QCExtraction = 'QC Extraction',
    Amplification = 'Amplification',
    QCAmplification = 'QC Amplification',
    Sequencing = 'Sequencing',
    DataAnalysis = 'Data Analysis',
    QCData = 'QC Data',
    Reporting = 'Reporting',
    Released = 'Released',
    Rejected = 'Rejected',
}

interface UseKitsProps {
    memberId: number;
    reloadTrigger?: boolean;
    isAdmin?: boolean;
}

export function useKits({
    memberId,
    reloadTrigger = false,
    isAdmin = false,
}: UseKitsProps) {
    const kitTypes = useStore((state) => state.kitTypes);
    const [kits, setKits] = React.useState<Kit[]>([]);

    React.useEffect(() => {
        if (!kitTypes?.length || memberId < 1) {
            setKits([]);
            return;
        }

        const newKits: Kit[] = [];
        let pendingRequests = kitTypes.length;

        const handleRequestResponse = (
            kitType: KitType,
            response: APIResponse
        ) => {
            if (response.success && response.kits) {
                const modifiedKits = response.kits.map((kit) => ({
                    ...kit,
                    KitTypeId: kitType.KitTypeId,
                }));
                newKits.push(...modifiedKits);
            }
            pendingRequests--;

            if (pendingRequests === 0) {
                setKits(
                    newKits.sort((a, b) => {
                        const dateA = new Date(a.InsertionDate);
                        const dateB = new Date(b.InsertionDate);
                        return dateB.getTime() - dateA.getTime();
                    })
                );
            }
        };

        for (const kitType of kitTypes) {
            if (isAdmin) {
                sendAdminRequest(
                    {
                        endpoint: '/member/get-kits',
                        body: {
                            memberId,
                            kitTypeId: kitType.KitTypeId,
                        },
                    },
                    (response: APIResponse) =>
                        handleRequestResponse(kitType, response)
                );
            } else {
                sendRequest(
                    {
                        endpoint: '/member/get-kits',
                        body: {
                            memberId,
                            kitTypeId: kitType.KitTypeId,
                        },
                    },
                    (response: APIResponse) =>
                        handleRequestResponse(kitType, response)
                );
            }
        }
    }, [kitTypes, memberId, reloadTrigger, isAdmin]);
    return kits;
}

interface APIResponse {
    success: boolean;
    errCode?: 'invalid_token' | 'auth_failed' | 'access_denied';
    errDetails?: string;
    kits?: Kit[];
}

export default useKits;
